import React from "react";
import { defineMessages, FormattedMessage as T, useIntl } from "react-intl";
import { makeStyles, Theme, darken } from "@material-ui/core/styles";
import clsx from "clsx";
import Button from "@material-ui/core/Button";
import FpsDialog from "./FpsDialog";
import NfpsDialog from "./NfpsDialog";
import {
  CityConfig,
  ControlReview,
  Ticket,
  TicketProfile,
  RuleFieldConfig,
  JsonPrimitive,
  RuleConditionWrapper,
} from "@egis-smartcity/parking";
import { ControlFormValues, getControlFormSchema } from "../ControlForm";
import {
  LocationControlFormValues,
  getLocationControlFormSchema,
} from "../LocationControl";
import * as yup from "yup";
import FullscreenNotification, {
  FullscreenNotificationProps,
} from "../../FullscreenNotification";
import useCityConfig from "../../../hooks/useCityConfig";
import moment from "moment";
import useLayoutPortal from "../../../hooks/useLayoutPortal";
import DistanceMismatchDialog from "./DistanceMismatchDialog";

export enum Action {
  EMIT_FPS = "emitFps",
  EMIT_NFPS = "emitNfps",
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    height: 40,
    display: "flex",
    flexDirection: "row",
    "& > *": {
      flex: 1,
      margin: "0px 8px",
      color: theme.palette.common.white,
    },
    "& > :last-child": {
      marginRight: 0,
    },
    "& > :first-child": {
      marginLeft: 0,
    },
  },
  emitNfpsButton: {
    height: 40,
    backgroundColor: "#014f57",
    "&:hover": {
      backgroundColor: darken("#014f57", 0.1),
      "@media (hover: none)": {
        backgroundColor: "#014f57",
      },
    },
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
  emitFpsButton: {
    height: 40,
    backgroundColor: "#f18335",
    "&:hover": {
      backgroundColor: darken("#f18335", 0.1),
      "@media (hover: none)": {
        backgroundColor: "#f18335",
      },
    },
    fontSize: "0.875rem",
    fontWeight: "bold",
  },
}));

const msg = defineMessages({
  [Action.EMIT_FPS]: {
    id: "actions.fpsAction.label",
    description: "Label of the emit FPS action button.",
    defaultMessage: "FPS",
  },
  [Action.EMIT_NFPS]: {
    id: "actions.nfpsAction.label",
    description: "Label of the emit NFPS action button.",
    defaultMessage: "NFPS",
  },
  invalidDataMissing: {
    id: "actions.invalidDataMissing.message",
    description: "Message of the notification when data is missing.",
    defaultMessage: "Please, fill the required fields of the control form.",
  },
  invalidPhotoNumber: {
    id: "actions.invalidPhotoNumber.message",
    description: "Message of the  notification when no enough photos selected.",
    defaultMessage: "Please, select {imageNumber} photo(s).",
  },
  invalidPhotoNumberFullPicturesMode: {
    id: "actions.invalidPhotoNumberFullPicturesMode.message",
    description: "Message of the  notification when no enough photos selected.",
    defaultMessage: "Please, select at least {imageNumber} photo(s).",
  },
});

type FieldData = {
  controlValidity?: boolean;
  controlVehicleType?: string | undefined | null;
  ticketProfile?: TicketProfile;
  ticketTariffCode?: string | undefined;
  ticketLocationCode?: string | undefined;
  ticketVehicleCategory?: string | undefined;
};

const getValidity = (end: string, controlDate: string) => {
  return moment(end).diff(moment(controlDate)) > 0;
};

function validCondition(
  fieldData: FieldData,
  conditionName: RuleFieldConfig,
  conditionValue: JsonPrimitive | Array<JsonPrimitive> | undefined
) {
  if (fieldData[conditionName] === undefined) return false;
  if (Array.isArray(conditionValue)) {
    return conditionValue.includes(fieldData[conditionName] as JsonPrimitive);
  } else {
    return fieldData[conditionName] === conditionValue;
  }
}

function IsFpsEnabled(
  ticketValidity: boolean,
  tickets: Ticket[],
  typeSynchro: string[],
  controlReview: ControlReview,
  cityConfig?: CityConfig,
  controlFormValues?: ControlFormValues
) {
  const conditionsGroupList = cityConfig?.review.ticket.allowsFPS;

  if (!conditionsGroupList) {
    for (let i = 0; i < tickets.length; i++) {
      if (typeSynchro.includes(tickets[i].profile)) {
        return true;
      }
    }

    return !ticketValidity;
  }

  return conditionsGroupList.some((conditionsGroup) => {
    return conditionsGroup.every((conditionItem) => {
      return isRuleValid(
        conditionItem,
        tickets,
        controlReview,
        ticketValidity,
        controlFormValues
      );
    });
  });
}

function isRuleValid(
  conditionItem: RuleConditionWrapper,
  tickets: Ticket[],
  controlReview: ControlReview,
  ticketValidity: boolean,
  controlFormValues?: ControlFormValues
): boolean {
  if (conditionItem.conditionWith) {
    for (let conditionName in conditionItem.conditionWith) {
      let conditionValue =
        conditionItem.conditionWith[conditionName as RuleFieldConfig];
      let isValid = verifyControlCondition(
        conditionName as RuleFieldConfig,
        conditionValue,
        tickets,
        controlReview,
        ticketValidity,
        controlFormValues
      );

      if (!isValid) return false;
    }
  }

  if (conditionItem.conditionWithout) {
    for (let conditionName in conditionItem.conditionWithout) {
      let conditionValue =
        conditionItem.conditionWithout[conditionName as RuleFieldConfig];
      let isValid = verifyControlCondition(
        conditionName as RuleFieldConfig,
        conditionValue,
        tickets,
        controlReview,
        ticketValidity,
        controlFormValues
      );

      if (isValid) return false;
    }
  }
  return true;
}

function verifyControlCondition(
  conditionName: RuleFieldConfig,
  conditionValue: JsonPrimitive | JsonPrimitive[] | undefined,
  tickets: Ticket[],
  controlReview: ControlReview,
  ticketValidity: boolean,
  controlFormValues?: ControlFormValues
) {
  if (conditionName.startsWith("ticket")) {
    return tickets.some((ticket) => {
      if (
        !controlReview.control?.date ||
        !getValidity(ticket.endDateTime, controlReview.control.date)
      ) {
        return false;
      }

      const ticketInfos: FieldData = {
        ticketProfile: ticket.profile,
        ticketTariffCode: ticket.tariffCode,
        ticketLocationCode: ticket.locationCode,
        ticketVehicleCategory: ticket.extraProperties?.vehicleType,
      };

      return validCondition(ticketInfos, conditionName, conditionValue);
    });
  } else {
    const controlInfos = {
      controlValidity: ticketValidity,
      controlVehicleType: controlFormValues?.vehicleType.value,
    };
    return validCondition(controlInfos, conditionName, conditionValue);
  }
}

export type ActionsProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> & {
  controlReview?: ControlReview;
  controlFormValues: ControlFormValues;
  locationControlFormValues: LocationControlFormValues;
  tickets?: Ticket[];
  ticketsValidity: boolean;
  licensePlateImageUrl?: string;
  panoramicImagesUrls?: string[];
  onActionDone(force?: number): void;
  onValidationFailure?(): void;
};

function Actions(props: ActionsProps) {
  ///////////////// PROPS, HOOKS AND STATE SETUP////////////////////////////////

  const {
    className,
    controlReview,
    controlFormValues,
    locationControlFormValues,
    tickets,
    ticketsValidity,
    licensePlateImageUrl,
    panoramicImagesUrls,
    onActionDone,
    onValidationFailure,
    ...other
  } = props;
  const { formatMessage: t } = useIntl();
  const { cityConfig } = useCityConfig();
  const [action, setAction] = React.useState<Action>();
  const [notification, setNotification] =
    React.useState<FullscreenNotificationProps>({ open: false });
  // const [errorCount, setErrorCount] = React.useState(0);
  const { isFpsLimitExceeded } = useLayoutPortal();
  const [confirmDistanceOpen, setConfirmDistanceOpen] = React.useState(false);

  const handleConfirmDistanceYes = React.useCallback(() => {
    setConfirmDistanceOpen(false);
    setAction(Action.EMIT_FPS);
  }, []);

  const handleConfirmDistanceNo = React.useCallback(() => {
    setConfirmDistanceOpen(false);
  }, []);

  ///////////////// CSS ////////////////////////////////////////////////////////

  const classes = useStyles();
  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);

  ///////////////// INIT AND SIDE EFFECT(S) ////////////////////////////////////

  // Reset when there is no more control review to process.
  React.useEffect(() => {
    if (controlReview !== undefined) return;

    setAction(undefined);
  }, [controlReview]);

  ///////////////// WORKFLOW ///////////////////////////////////////////////////

  const handleEmitFpsButtonClick = React.useCallback(
    async (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const controlFormValid = await getControlFormSchema(
        yup,
        t,
        false
      ).isValid(controlFormValues);
      const locationControlFormValid = await getLocationControlFormSchema(
        yup,
        t,
        cityConfig?.review?.dedicatedStreetNumber,
        cityConfig?.review?.zipcodesAllowed || [],
        cityConfig?.review.citiesAllowed || [],
        false,
        cityConfig?.review?.nfpsOptionalFieldsEnabled === true
      ).isValid(locationControlFormValues);

      if (!controlFormValid || !locationControlFormValid) {
        if (onValidationFailure) onValidationFailure();
        setNotification({
          open: true,
          message: <T {...msg.invalidDataMissing} />,
          type: "error",
        });
        setTimeout(() => {
          setNotification({
            open: false,
          });
        }, 3000);
        return;
      }

      const enoughPanoramicImages =
        cityConfig?.review?.fps?.picturesNumber === undefined ||
        (panoramicImagesUrls !== undefined &&
        cityConfig.review.fullPictures === true
          ? panoramicImagesUrls.length >= cityConfig.review.fps.picturesNumber
          : panoramicImagesUrls!.length ===
            cityConfig.review.fps.picturesNumber);

      if (!enoughPanoramicImages) {
        if (onValidationFailure) onValidationFailure();
        setNotification({
          open: true,
          message:
            cityConfig?.review?.fullPictures === true ? (
              <T
                {...msg.invalidPhotoNumberFullPicturesMode}
                values={{
                  imageNumber: cityConfig?.review?.fps?.picturesNumber,
                }}
              />
            ) : (
              <T
                {...msg.invalidPhotoNumber}
                values={{
                  imageNumber: cityConfig?.review?.fps?.picturesNumber,
                }}
              />
            ),
          type: "error",
        });
        setTimeout(() => {
          setNotification({
            open: false,
          });
        }, 3000);
        return;
      }

      const distanceMismatch =
        locationControlFormValues?.distanceMismatch ?? null;

      const DISTANCE_THRESHOLD =
        Number(process.env.REACT_APP_DISTANCE_MISMATCH_THRESHOLD) || 20;

      if (
        typeof distanceMismatch === "number" &&
        distanceMismatch > DISTANCE_THRESHOLD
      ) {
        setConfirmDistanceOpen(true);
        return;
      }

      setAction(Action.EMIT_FPS);
    },
    [
      cityConfig,
      controlFormValues,
      locationControlFormValues,
      onValidationFailure,
      panoramicImagesUrls,
      t,
    ]
  );

  const handleEmitNfpsButtonClick = React.useCallback(
    async (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      const controlFormValid = await getControlFormSchema(
        yup,
        t,
        true,
        cityConfig?.review?.nfpsOptionalFieldsEnabled === true
      ).isValid(controlFormValues);
      const locationControlFormValid = await getLocationControlFormSchema(
        yup,
        t,
        cityConfig?.review.dedicatedStreetNumber,
        cityConfig?.review?.zipcodesAllowed || [],
        cityConfig?.review.citiesAllowed || [],
        true,
        cityConfig?.review?.nfpsOptionalFieldsEnabled === true
      ).isValid(locationControlFormValues);

      if (!controlFormValid || !locationControlFormValid) {
        if (onValidationFailure) onValidationFailure();
        setNotification({
          open: true,
          message: <T {...msg.invalidDataMissing} />,
          type: "error",
        });
        setTimeout(() => {
          setNotification({
            open: false,
          });
        }, 3000);
        return;
      }

      setAction(Action.EMIT_NFPS);
    },
    [
      controlFormValues,
      locationControlFormValues,
      onValidationFailure,
      t,
      cityConfig?.review.dedicatedStreetNumber,
      cityConfig?.review?.zipcodesAllowed,
      cityConfig?.review.citiesAllowed,
      cityConfig?.review?.nfpsOptionalFieldsEnabled,
    ]
  );

  const handleDialogClose = React.useCallback(() => setAction(undefined), []);

  const handleNotificationClose = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>) => {
      setNotification({ open: false });
    },
    []
  );

  return (
    <>
      <div className={rootClasses} {...other}>
        <Button
          className={classes.emitNfpsButton}
          variant="contained"
          onClick={handleEmitNfpsButtonClick}
        >
          <T {...msg[Action.EMIT_NFPS]} />
        </Button>
        <Button
          className={classes.emitFpsButton}
          variant="contained"
          disabled={
            isFpsLimitExceeded ||
            !IsFpsEnabled(
              ticketsValidity,
              tickets || [],
              //@ts-ignore
              cityConfig?.review?.ticket?.typeSynchro || [],
              controlReview || {},
              cityConfig,
              controlFormValues
            )
          }
          onClick={handleEmitFpsButtonClick}
        >
          <T {...msg[Action.EMIT_FPS]} />
        </Button>
      </div>
      {controlReview !== undefined &&
        tickets !== undefined &&
        licensePlateImageUrl !== undefined &&
        panoramicImagesUrls !== undefined && (
          <NfpsDialog
            open={action === Action.EMIT_NFPS}
            controlReview={controlReview}
            controlForm={controlFormValues}
            locationControlFormValues={locationControlFormValues}
            tickets={tickets}
            plateImage={licensePlateImageUrl}
            imageSelection={panoramicImagesUrls}
            onCloseDialog={handleDialogClose}
            onNfpsSent={onActionDone}
          />
        )}
      {controlReview !== undefined &&
        licensePlateImageUrl !== undefined &&
        panoramicImagesUrls !== undefined && (
          <FpsDialog
            open={action === Action.EMIT_FPS}
            controlReview={controlReview}
            controlForm={controlFormValues}
            locationControlFormValues={locationControlFormValues}
            plateImage={licensePlateImageUrl}
            imageSelection={panoramicImagesUrls}
            onCloseDialog={handleDialogClose}
            onFpsSent={onActionDone}
            tickets={tickets || []}
          />
        )}
      <DistanceMismatchDialog
        open={confirmDistanceOpen}
        distance={
          typeof locationControlFormValues.distanceMismatch === "number"
            ? locationControlFormValues.distanceMismatch
            : 0
        }
        onConfirm={handleConfirmDistanceYes}
        onCancel={handleConfirmDistanceNo}
      />
      <FullscreenNotification
        onCloseButtonClick={handleNotificationClose}
        {...notification}
      />
    </>
  );
}

export default Actions;
